import {
  Box,
  ContextualPopup,
  Divider,
  Flex,
  Progress,
  Reveal,
  Typography,
} from '@/ui';
import { useIsInViewport } from '@/hooks/useIsInViewport';
import type { FC } from 'react';
import { Fragment, useMemo } from 'react';
import { messages } from '../messages';
import type { DataBlocks } from '../types';
import { useTruth } from './useTruth';

export const useDataPopup = () => {
  const { elementRef, isUnderViewport } = useIsInViewport();

  const DataPopup = useMemo<
    FC<{
      heading?: string;
      subheading?: React.ReactNode;
      handle: React.ReactNode;
      dataVisualisation?: React.ReactNode;
      condition?: {
        title: string;
        options: {
          title: string;
          value?: string;
          selected?: boolean;
        }[];
      };
      confidence?: {
        rangeLow: number;
        rangeHigh: number;
      };
      dataBlocks?: Partial<DataBlocks>;
    }>
  >(
    () =>
      ({
        handle,
        heading,
        subheading,
        dataVisualisation,
        condition,
        confidence,
        dataBlocks: dataBlocksObject = {},
      }) => {
        const { revealed } = useTruth();
        const dataBlocks = Object.keys(dataBlocksObject)
          .map((key) => ({
            key,
            ...dataBlocksObject[key as keyof DataBlocks],
          }))
          .filter((block) => !!block.value);

        const content = !revealed ? (
          <>{handle}</>
        ) : (
          <Typography.Mark ref={elementRef}>
            <ContextualPopup
              id={handle?.toString()}
              handle={handle}
              behaviour="click"
            >
              {heading && (
                <ContextualPopup.Heading>{heading}</ContextualPopup.Heading>
              )}
              {condition && (
                <ContextualPopup.Section>
                  <Flex justifyContent="flex-start" alignItems="center">
                    <Box marginRight={24}>
                      <Typography.Caption opacity={0.5} whiteSpace="nowrap">
                        {condition.title}
                      </Typography.Caption>
                    </Box>

                    <Box paddingRight={16}>
                      {condition.options.map(
                        ({ title, value, selected }, index) => (
                          <Box marginTop={index === 0 ? 0 : 12} key={title}>
                            <Typography.Caption
                              fontWeight={
                                selected || handle === value
                                  ? 'bold'
                                  : undefined
                              }
                              opacity={selected || handle === value ? 1 : 0.5}
                              whiteSpace="nowrap"
                            >
                              {title} →{' '}
                              {value ? (
                                <Typography.Mark>{value}</Typography.Mark>
                              ) : null}
                            </Typography.Caption>
                          </Box>
                        )
                      )}
                    </Box>
                  </Flex>
                </ContextualPopup.Section>
              )}
              {dataVisualisation && (
                <>
                  {condition && <Divider.Horizontal />}
                  {dataVisualisation}
                </>
              )}
              {subheading && (
                <>
                  {(dataVisualisation || condition) && <Divider.Horizontal />}
                  <ContextualPopup.Section>
                    <Typography.Caption textAlign="center">
                      {subheading}
                    </Typography.Caption>
                  </ContextualPopup.Section>
                </>
              )}
              {confidence && (
                <>
                  {(dataVisualisation || condition || dataVisualisation) && (
                    <Divider.Horizontal />
                  )}
                  <ContextualPopup.Section>
                    <Progress
                      max={100}
                      maxLabel={
                        dataBlocks.length
                          ? `${dataBlocks.length} data source/s`
                          : ''
                      }
                      value={Math.round(confidence.rangeLow)}
                      valueLabel={`Confidence Level: ${Math.round(
                        confidence.rangeLow
                      )}-${Math.round(confidence.rangeHigh)}%`}
                      pendingValue={Math.round(confidence.rangeHigh)}
                    />
                  </ContextualPopup.Section>
                </>
              )}
              {dataBlocks.map(({ title, value, access, key }) => (
                <Fragment key={key}>
                  <Divider.Horizontal />

                  <ContextualPopup.Section>
                    <Flex justifyContent="space-between">
                      <Typography.Caption>{title}</Typography.Caption>
                      <Typography.Caption>{value}</Typography.Caption>
                    </Flex>

                    <Reveal inactiveOpacity={0.25} activeOpacity={0.5}>
                      <Typography.Caption>
                        {access === 'any-website' &&
                          messages['access.anyWebsite']}
                        {access === 'any-website-guess' &&
                          messages['access.anyWebsiteGuess']}
                        {access === 'any-website-or-requires-permission' &&
                          messages['access.anyWebsiteOrRequiresPermission']}
                      </Typography.Caption>
                    </Reveal>
                  </ContextualPopup.Section>
                </Fragment>
              ))}
            </ContextualPopup>
          </Typography.Mark>
        );

        return <span ref={elementRef}>{content}</span>;
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- elementRef is a ref; its identity is stable
    []
  );

  return {
    DataPopup,
    canUpdate: isUnderViewport,
  };
};
